import { JupiterProvider, type JupiterSwapExecuteResponse } from "@solomon-labs/swap-client";

import {
    type JupiterQuoteParams,
    type JupiterUltraQuoteResponse,
    parseJupiterUltraQuotePayload
} from "@/hooks/swap/jupiterPayload";

const JUPITER_BASE_URL = "https://jupiter.solomonlabs.io";

export const jupiterProvider = new JupiterProvider({
    baseUrl: JUPITER_BASE_URL,
    timeoutMs: 30000
});

export { JUPITER_BASE_URL };

export const JUPITER_SWAP_BASE_URL = `${JUPITER_BASE_URL}/swap/v2`;

/** Swap V2 execution; the installed SDK still targets Ultra V1. */
export async function executeJupiterSwap(request: {
    requestId: string;
    signedTransaction: string;
}): Promise<JupiterSwapExecuteResponse> {
    const response = await fetch(`${JUPITER_SWAP_BASE_URL}/execute`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(request),
        signal: AbortSignal.timeout(30000)
    });
    const payload: unknown = await response.json();
    if (!response.ok) {
        const message =
            payload && typeof payload === "object" && "error" in payload && typeof payload.error === "string"
                ? payload.error
                : `Swap execution failed: ${response.status}`;
        throw new Error(message);
    }
    if (!payload || typeof payload !== "object" || !("status" in payload)) {
        throw new Error("Invalid swap execution response");
    }
    if (payload.status !== "Success" && payload.status !== "Failed") {
        throw new Error("Invalid swap execution status");
    }
    if (
        payload.status === "Success" &&
        (!("signature" in payload) || typeof payload.signature !== "string" || !payload.signature)
    ) {
        throw new Error("Swap execution response has no signature");
    }
    return payload as JupiterSwapExecuteResponse;
}

type JsonRecord = Record<string, unknown>;
export async function fetchSwapQuote(params: JupiterQuoteParams): Promise<JupiterUltraQuoteResponse> {
    const queryParams = new URLSearchParams({
        inputMint: params.inputMint,
        outputMint: params.outputMint,
        amount: params.amount
    });

    if (params.taker) {
        queryParams.set("taker", params.taker);
    }

    const response = await fetch(`${JUPITER_SWAP_BASE_URL}/order?${queryParams.toString()}`, {
        cache: "no-store",
        headers: {
            Accept: "application/json",
            "Content-Type": "application/json"
        }
    });

    if (!response.ok) {
        const payload = (await response.json().catch(() => null)) as JsonRecord | null;
        const message =
            payload && typeof payload.message === "string"
                ? payload.message
                : payload && typeof payload.error === "string"
                  ? payload.error
                  : `Order failed: ${response.status}`;
        throw new Error(message);
    }

    return parseJupiterUltraQuotePayload((await response.json()) as unknown, {
        requestedTaker: params.taker
    });
}
